import {
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Row,
  Select,
  Typography,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type { FormInstance } from 'antd';
import { type StaffInput } from '@/api/hr';
import { PhotoUpload } from '@/shared/components/photo-upload';
import { useHrLookups } from './lookups';

export interface StaffFormShape {
  firstName: string;
  lastName: string;
  dateOfBirth?: Dayjs;
  genderId?: number;
  photoPath?: string | null;
  cnic?: string;
  addressLine?: string;
  cityId?: number;
  phone?: string;
  designationId?: number;
  departmentId?: number;
  employmentTypeId: number;
  joiningDate: Dayjs;
  bankName?: string;
  bankAccountNumber?: string;
  bankBranch?: string;
  iban?: string;
  emergencyContactName?: string;
  emergencyContactPhone?: string;
  emergencyRelation?: string;
}

export function valuesToInput(values: StaffFormShape): StaffInput {
  return {
    firstName: values.firstName.trim(),
    lastName: values.lastName.trim(),
    dateOfBirth: values.dateOfBirth ? values.dateOfBirth.format('YYYY-MM-DD') : null,
    genderId: values.genderId ?? null,
    photoPath: values.photoPath ?? null,
    cnic: values.cnic?.trim() || undefined,
    addressLine: values.addressLine?.trim() || undefined,
    cityId: values.cityId ?? null,
    phone: values.phone?.trim() || undefined,
    designationId: values.designationId ?? null,
    departmentId: values.departmentId ?? null,
    employmentTypeId: values.employmentTypeId,
    joiningDate: values.joiningDate.format('YYYY-MM-DD'),
    bankName: values.bankName?.trim() || undefined,
    bankAccountNumber: values.bankAccountNumber?.trim() || undefined,
    bankBranch: values.bankBranch?.trim() || undefined,
    iban: values.iban?.trim() || undefined,
    emergencyContactName: values.emergencyContactName?.trim() || undefined,
    emergencyContactPhone: values.emergencyContactPhone?.trim() || undefined,
    emergencyRelation: values.emergencyRelation?.trim() || undefined,
  };
}

export function StaffForm({
  form,
  layout = 'cards',
  onFinish,
  onPhotoPendingChange,
}: {
  form: FormInstance<StaffFormShape>;
  layout?: 'cards' | 'inline';
  onFinish: (values: StaffFormShape) => void;
  /** Wired to the PhotoUpload's pending file. Pair with `usePhotoUpload('staff-photos')` in the parent. */
  onPhotoPendingChange?: (file: File | null) => void;
}) {
  const lookups = useHrLookups();
  const watchedDepartment = Form.useWatch('departmentId', form);
  const opt = (rows: { id: number; name: string }[]) =>
    rows.map((r) => ({ value: r.id, label: r.name }));
  const cityOpt = (rows: { id: number; name: string; area: string | null }[]) =>
    rows.map((r) => ({ value: r.id, label: r.area ? `${r.name} · ${r.area}` : r.name }));
  const designationOptions = lookups.designations
    .filter((d) => watchedDepartment == null || d.departmentId === watchedDepartment)
    .map((d) => ({ value: d.id, label: d.name }));

  const Wrap = layout === 'cards' ? CardWrap : InlineWrap;

  return (
    <Form form={form} layout="vertical" onFinish={onFinish}>
      <Wrap title="Identity">
        <Row gutter={12}>
          <Col xs={24}>
            <Form.Item label="Photo" name="photoPath">
              <PhotoUpload category="staff-photos" onPendingChange={onPhotoPendingChange} />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="First name" name="firstName" rules={[{ required: true, max: 100 }]}>
              <Input autoFocus />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item label="Last name" name="lastName" rules={[{ required: true, max: 100 }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Date of birth" name="dateOfBirth">
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" disabledDate={(d) => d && d.isAfter(dayjs())} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Gender" name="genderId">
              <Select allowClear options={opt(lookups.genders)} placeholder="—" />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="CNIC" name="cnic" rules={[{ max: 15 }]}>
              <Input placeholder="35202-xxxxxxx-x" />
            </Form.Item>
          </Col>
          <Col xs={24} md={16}>
            <Form.Item label="Address" name="addressLine">
              <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="City" name="cityId">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={cityOpt(lookups.cities)}
                placeholder="—"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Phone" name="phone" rules={[{ max: 20 }]}>
              <Input placeholder="03xx-xxxxxxx" />
            </Form.Item>
          </Col>
        </Row>
      </Wrap>

      <Wrap title="Employment">
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item label="Department" name="departmentId">
              <Select
                allowClear
                options={opt(lookups.departments)}
                showSearch
                optionFilterProp="label"
                placeholder="—"
                onChange={() => form.setFieldValue('designationId', undefined)}
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Designation" name="designationId">
              <Select
                allowClear
                options={designationOptions}
                showSearch
                optionFilterProp="label"
                placeholder="—"
              />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Employment type" name="employmentTypeId" rules={[{ required: true }]}>
              <Select options={opt(lookups.employmentTypes)} />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Joining date" name="joiningDate" rules={[{ required: true }]}>
              <DatePicker style={{ width: '100%' }} format="DD MMM YYYY" />
            </Form.Item>
          </Col>
        </Row>
      </Wrap>

      <Wrap title="Bank details">
        <Row gutter={12}>
          <Col xs={24} md={8}>
            <Form.Item label="Bank name" name="bankName" rules={[{ max: 100 }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Account number" name="bankAccountNumber" rules={[{ max: 30 }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Branch" name="bankBranch" rules={[{ max: 100 }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="IBAN" name="iban" rules={[{ max: 30 }]}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Wrap>

      <Wrap title="Emergency contact">
        <Row gutter={12}>
          <Col xs={24} md={10}>
            <Form.Item label="Contact name" name="emergencyContactName" rules={[{ max: 200 }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={8}>
            <Form.Item label="Phone" name="emergencyContactPhone" rules={[{ max: 20 }]}>
              <Input />
            </Form.Item>
          </Col>
          <Col xs={24} md={6}>
            <Form.Item label="Relation" name="emergencyRelation" rules={[{ max: 100 }]}>
              <Input />
            </Form.Item>
          </Col>
        </Row>
      </Wrap>
    </Form>
  );
}

function CardWrap({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Card title={<Typography.Text strong>{title}</Typography.Text>} style={{ marginBottom: 16 }}>
      {children}
    </Card>
  );
}
function InlineWrap({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <Typography.Text strong style={{ display: 'block', marginBottom: 8 }}>{title}</Typography.Text>
      {children}
    </div>
  );
}
